// "Not the right one…" (decisions 2026-10-18): an item linked to the wrong
// entry (a misread cover, a wrong pick) is relinked to the right one. The
// member searches the type's sources from the item's title, picks, sees what
// would change (POST …/relink with preview), confirms, and the server
// replaces what came from the wrong entry — the copy's own fields, the place
// and everyone's opinions stay.
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { asTags } from '../../fieldTypes';
import { fieldKey } from '../../fieldKeys';
import { selectLabel, uiLang } from '../../kinds';
import { sourceLabel, useSources } from '../../sources';
import TextSearchDialog from '../../scan/TextSearchDialog';
import { errorMessage } from '../../errors';
import { Alert, Checkbox, useConfirm } from '../../ui';
import { FieldDisplay } from '../FieldValue';
import { refreshError } from '../ItemForm';

/** The words of a kind: a film, a series, a book; anything else "the right one". */
export const relinkWords = (preset) => (['movie', 'tv', 'book'].includes(preset) ? preset : 'other');

const SHOWN_NAMES = 3;

/** A value of the preview, short: a few names of a long list, a long text cut by its class. */
function Value({ field, value }) {
  const { t } = useTranslation();
  if (value === null || value === undefined) return <em className="relink-preview__none">{t('relink.preview.none')}</em>;
  if (field && field.type === 'tags') {
    const names = asTags(value);
    const more = names.length - SHOWN_NAMES;
    return <span>{names.slice(0, SHOWN_NAMES).join(', ')}{more > 0 ? ` ${t('relink.preview.more', { count: more })}` : ''}</span>;
  }
  // A year is not a quantity ("1975", not "1,975").
  return field && field.role !== 'year' ? <FieldDisplay field={field} value={value} /> : <span>{String(value)}</span>;
}

/** The preview's warnings worth saying: a value refused, a field the mapping cannot fill, a source that did not answer. */
const SHOWN_WARNINGS = ['refused', 'field_missing', 'source_skipped', 'image_not_stored', 'platform_not_listed'];
export const relinkWarnings = (warnings) => (warnings || []).filter(w => SHOWN_WARNINGS.includes(w.code));

/** One warning in words. */
export const warningText = (t, warning, name, sourceName, valueName = (key, value) => value) => t(`relink.warnings.${warning.code}`, {
  field: warning.field ? name(warning.field) : '', reason: warning.reason || '', source: warning.source ? sourceName(warning.source) : '',
  value: warning.value !== undefined ? valueName(warning.field, warning.value) : '',
});

/**
 * What a relink would change: each field before → after, the images, the
 * copy's codes and posters of unknown origin it may also replace (the
 * member's choices, reported through onChoices), the same entry already
 * here, what the mapping could not do, what stays.
 */
export function RelinkPreview({ preview, fields, sourceName, onChoices, words = 'other' }) {
  const { t, i18n } = useTranslation();
  const sources = useSources();
  const lang = uiLang(i18n);
  const byKey = new Map((fields || []).map(field => [fieldKey(field), field]));
  const name = (key) => (byKey.get(key) || {}).name || key;
  const changes = preview.changes || [];
  const images = (preview.images || []).filter(image => image.action !== 'unchanged');
  const unknown = (preview.images || []).filter(image => image.unknown > 0);
  const identifiers = preview.identifiers || [];
  const warnings = relinkWarnings(preview.warnings);
  // An unknown poster alone in its field is most likely the wrong film's (downloaded before the origin was kept): ticked.
  const [choices, setChoices] = useState(() => ({
    identifiers: [], unknownImages: unknown.some(image => image.unknown === 1 && image.count === 1),
  }));
  const choose = (next) => { setChoices(next); if (onChoices) onChoices(next); };
  useEffect(() => { if (onChoices) onChoices(choices); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="relink-preview">
      {(preview.duplicates || []).length > 0 && (
        <Alert tone="warning">
          {t('relink.preview.duplicate', { count: preview.duplicates.length, titles: preview.duplicates.map(d => d.title).join(', ') })}
        </Alert>
      )}
      {changes.length + images.length > 0 ? (
        <dl className="relink-preview__list" aria-label={t('relink.preview.label')}>
          {changes.map(change => (
            <div key={change.key} className="relink-preview__row">
              <dt>{name(change.key)}</dt>
              <dd>
                {change.before !== null && change.before !== undefined && (
                  <><del className="relink-preview__before"><Value field={byKey.get(change.key)} value={change.before} /></del>{' → '}</>
                )}
                {change.after === null || change.after === undefined
                  ? <em className="relink-preview__cleared">{t('relink.preview.cleared')}</em>
                  : <ins className="relink-preview__after"><Value field={byKey.get(change.key)} value={change.after} /></ins>}
              </dd>
            </div>
          ))}
          {images.map(image => (
            <div key={image.key} className="relink-preview__row">
              <dt>{name(image.key)}</dt>
              <dd>
                {t(`relink.preview.image.${image.action}`, { source: sourceName })}
                {image.kept > 0 && ` ${t('relink.preview.imageKept', { count: image.kept })}`}
              </dd>
            </div>
          ))}
        </dl>
      ) : <p>{t('relink.preview.nothing')}</p>}
      {unknown.length > 0 && (
        <div className="relink-preview__choice">
          <p className="relink-preview__choice-title">{t('relink.preview.unknownImage', { count: unknown[0].unknown })}</p>
          <Checkbox label={unknown.some(image => image.new_image) ? t(`relink.preview.replaceUnknown.${words}`) : t('relink.preview.removeUnknown')}
                    checked={choices.unknownImages} onChange={(event) => choose({ ...choices, unknownImages: event.target.checked })} />
        </div>
      )}
      {identifiers.map(identifier => (
        <div key={identifier.key} className="relink-preview__choice">
          <p className="relink-preview__choice-title">
            {t('relink.preview.identifierKept', { field: name(identifier.key), before: String(identifier.before), after: String(identifier.after) })}
          </p>
          <Checkbox label={t(`relink.preview.replaceIdentifier.${identifier.scheme}`, { defaultValue: t('relink.preview.replaceIdentifier.other', { field: name(identifier.key) }) })}
                    checked={choices.identifiers.includes(identifier.scheme)}
                    onChange={(event) => choose({ ...choices, identifiers: event.target.checked
                      ? [...choices.identifiers, identifier.scheme] : choices.identifiers.filter(s => s !== identifier.scheme) })} />
        </div>
      ))}
      {warnings.length > 0 && (
        <ul className="relink-preview__warnings" aria-label={t('relink.preview.warningsLabel')}>
          {warnings.map((warning, i) => (
            <li key={i}>{warningText(t, warning, name, (id) => sourceLabel(id, sources, lang),
              (key, value) => (byKey.get(key) && byKey.get(key).type === 'select' ? selectLabel(byKey.get(key), value, lang) : value))}</li>
          ))}
        </ul>
      )}
      <p className="ui-field__hint">{t('relink.preview.kept')}</p>
    </div>
  );
}

/** A relink's failure in words: its own codes (item changed, already this entry), else a source's. */
export const relinkError = (t, error) => {
  const code = error?.response?.data?.code;
  return ['item_changed', 'already_linked'].includes(code) ? errorMessage(t, error) : refreshError(t, error);
};

/**
 * open() shows the search; a pick asks for the preview, then the
 * confirmation (with the member's choices), then relinks and calls
 * onDone(answer, candidate). Failures go to onError(error). `dialog` is the
 * search to render.
 */
export function useRelink({ inventoryId, item, itemType, fields, title, lang, sourceName, onDone, onError }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const choices = useRef({ identifiers: [], unknownImages: false });
  const words = relinkWords(itemType && itemType.preset);
  const pick = async (candidate) => {
    setSearching(false);
    setBusy(true);
    const url = `/inventories/${inventoryId}/items/${item.id}/relink`;
    const body = { source: candidate.source, source_id: candidate.source_id };
    try {
      const { data: preview } = await api.post(url, { ...body, preview: true });
      choices.current = { identifiers: [], unknownImages: false };
      const yes = await confirm({
        title: t('relink.confirmTitle', { title: candidate.title }),
        message: <RelinkPreview preview={preview} fields={fields} sourceName={sourceName(candidate.source)} words={words}
                                onChoices={(next) => { choices.current = next; }} />,
        confirmLabel: t('relink.confirm'),
      });
      if (!yes) return;
      const { identifiers, unknownImages } = choices.current;
      const { data } = await api.post(url, {
        ...body,
        ...(identifiers.length ? { replace_identifiers: identifiers } : {}),
        ...(unknownImages ? { replace_unknown_images: true } : {}),
      });
      onDone(data, candidate);
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  };
  const dialog = searching ? (
    <TextSearchDialog itemType={itemType} lang={lang} initialQuery={title || ''}
      initialPlatform={itemType && itemType.preset === 'videogame' ? ((item && item.field_values && item.field_values.platform) || '') : undefined}
      title={t(`relink.title.${words}`)} description={t('relink.searchHint')} pickLabel={t('relink.pick')}
      onClose={() => setSearching(false)} onPick={pick} />
  ) : null;
  return { open: () => setSearching(true), busy, dialog, label: t(`relink.button.${words}`) };
}
